import { supabase } from './supabase';
import { AuthMachineContext, AuthState } from './authTransitions';

/**
 * Collaborators the Supabase auth-state listener needs from the owning machine.
 * `getState`/`setState` thread the machine's private `state` field so the
 * handler's INITIALIZING guard and transitions behave exactly as before.
 */
export interface AuthStateListenerDeps {
  context: AuthMachineContext;
  getState: () => AuthState;
  fetchUserProfile: (userId: string) => Promise<boolean>;
  transitionTo: (newState: AuthState) => void;
  notifyListeners: () => void;
  log: (message: string) => void;
  error: (message: string, error?: unknown) => void;
}

/**
 * Register the Supabase `onAuthStateChange` handler (SIGNED_IN / SIGNED_OUT /
 * TOKEN_REFRESHED / USER_UPDATED). Returns the subscription's `unsubscribe`
 * function. Split out of AuthStateMachine (STD-014); logic is unchanged.
 */
export function createAuthStateListener(deps: AuthStateListenerDeps): () => void {
  const { context, getState, fetchUserProfile, transitionTo, notifyListeners, log, error } = deps;

  const {
    data: { subscription },
  } = supabase.auth.onAuthStateChange(async (event, session) => {
    log(`Auth state changed: ${event}`);

    // Skip INITIAL_SESSION and SIGNED_IN events during initialization
    if (
      getState() === AuthState.INITIALIZING &&
      (event === 'INITIAL_SESSION' || event === 'SIGNED_IN')
    ) {
      log('Skipping auth event during initialization');
      return;
    }

    switch (event) {
      case 'SIGNED_IN':
        if (session?.user && getState() !== AuthState.AUTHENTICATED) {
          context.session = session;
          context.user = session.user;

          const profileLoaded = await fetchUserProfile(session.user.id);
          if (profileLoaded) {
            // Check if user has an organization assigned
            if (!context.profile?.organization_id) {
              // User is pending approval - sign them out and redirect
              log('User pending approval (no organization) - signing out');
              await supabase.auth.signOut();
              context.user = null;
              context.profile = null;
              context.session = null;
              transitionTo(AuthState.UNAUTHENTICATED);
              window.location.href = '/pending-approval';
            } else {
              transitionTo(AuthState.AUTHENTICATED);
            }
          } else {
            error('Profile loading failed during SIGNED_IN event');
            // Don't transition to unauthenticated - stay in current state to avoid loops
          }
        }
        break;

      case 'SIGNED_OUT':
        log('User signed out - clearing context');
        transitionTo(AuthState.UNAUTHENTICATED);
        break;

      case 'TOKEN_REFRESHED':
        if (session?.user) {
          log('Token refreshed successfully');
          context.session = session;
          context.user = session.user;
          // Keep current authenticated state
        }
        break;

      case 'USER_UPDATED':
        if (session?.user) {
          context.session = session;
          context.user = session.user;
          // Refresh profile data
          const profileUpdated = await fetchUserProfile(session.user.id);
          if (!profileUpdated) {
            error('Profile refresh failed after USER_UPDATED');
          }
          notifyListeners();
        }
        break;
    }
  });

  return subscription.unsubscribe;
}
